/**
 * useContentSearch — wrapper React Query attorno alla RPC `search_content`.
 *
 * Applica un debounce sul termine di ricerca per evitare richieste a ogni
 * battitura, e usa una chiave cache che include scope+id+termine così che
 * risultati identici non vengano richiesti due volte.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SearchScope } from "@/components/search/SearchProvider";

export interface SearchHit {
  page_id: string;
  page_title: string;
  page_slug: string | null;
  section_id: string;
  section_title: string;
  module_id: string;
  module_title: string;
  course_id: string;
  course_title: string;
  course_slug: string | null;
  snippet: string;
  rank: number;
}

/** Hook con debounce + React Query. */
export function useContentSearch(params: {
  query: string;
  scope: SearchScope;
  scopeId: string | null;
  enabled?: boolean;
}) {
  const { query, scope, scopeId, enabled = true } = params;

  // Debounce 250 ms del termine — riduce drasticamente le chiamate RPC.
  const [debounced, setDebounced] = useState(query);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const trimmed = debounced.trim();
  const canRun = enabled && trimmed.length >= 2;

  return useQuery({
    queryKey: ["content-search", scope, scopeId, trimmed],
    enabled: canRun,
    staleTime: 30_000,
    queryFn: async (): Promise<SearchHit[]> => {
      // Chiamata alla RPC: i tipi non sono ancora aggiornati nel client autogenerato.
      const { data, error } = await (supabase as any).rpc("search_content", {
        _query: trimmed,
        _scope: scope,
        _scope_id: scope === "site" ? null : scopeId,
        _limit: 80,
      });
      if (error) throw error;
      return (data ?? []) as SearchHit[];
    },
  });
}